import { MenuRadioGroup, MenuRadioItem, MenuSeparator } from "@salestrak/ui";

import { type UpdateCycle } from "#app/lib/project-updates";
import { formatUpdateCycle } from "#app/lib/project-updates";
import type { Omittable } from "@salestrak/ui";

// Day presets in the follow-up window grammar ("Every N days") — the two
// cycle menus read identically wherever they open. A stored cycle outside the
// ladder (set by an automation) joins the list under its own label so it
// stays selectable.
export const STAGE_CYCLE_PRESETS: UpdateCycle[] = [7, 14, 30, 60, 90].map(
  (amount) => ({ amount, step: "days" }),
);

/** What a cycle row stands for: a concrete cycle, "inherit" — follow the level
 *  below (a Stage following its Workflow's default) — or "off": this level
 *  doesn't ask for updates. Surfaces with no level below (the Workflow editor)
 *  simply never offer the inherit row. */
export type UpdateCyclePick = "inherit" | "off" | UpdateCycle;

const INHERIT_VALUE = "__inherit__";
const OFF_VALUE = "__off__";

/** The reason the off row gives while it is locked. */
export const FOLLOW_UPS_OFF_LOCKED_REASON = "Only Admins can turn follow ups off.";

const cycleValue = (cycle: UpdateCycle) => `${cycle.amount}-${cycle.step}`;

const pickValue = (pick: UpdateCyclePick): string =>
  pick === "inherit" ? INHERIT_VALUE : pick === "off" ? OFF_VALUE : cycleValue(pick);

const isPreset = (candidate: UpdateCycle) =>
  STAGE_CYCLE_PRESETS.some(
    (preset) =>
      preset.amount === candidate.amount && preset.step === candidate.step,
  );

// The cycle ladder as menu radio rows, shared by the Stage pin menu (board
// column ⋯ menu) and the Workflow default menu (workflow settings). The level's
// current state is the checked row; a pick commits at once and closes the
// menu, so there is no draft and no Save. The host owns the group label and
// any footnote; this owns the ladder, its inherit row and the off row with its
// permission reason.
export function UpdateCycleRows({
  value,
  onPick,
  storedCycle,
  inheritLabel,
  inheritDetail,
  offLocked,
}: Omittable<{
  /** The level's current state — the row that opens checked. */
  value: UpdateCyclePick;
  /** A pick that differs from `value`. The menu closes on its own. */
  onPick: (next: UpdateCyclePick) => void;
  /** The currently persisted cycle, so a non-preset value stays selectable. */
  storedCycle: UpdateCycle | null;
  /** When set, an inherit row leads the ladder (e.g. "Workflow default").
   *  Absent = no level below to inherit from. */
  inheritLabel?: string;
  /** What the inherited level resolves to (e.g. "Every 30 days"), as the
   *  inherit row's trailing meta. */
  inheritDetail?: string;
  /** Turning follow ups off is admin-territory (manage.stage_updates_off). The
   *  row stays visible and says why it is locked. */
  offLocked: boolean;
}>) {
  const cycleRows = [
    ...(storedCycle && !isPreset(storedCycle) ? [storedCycle] : []),
    ...STAGE_CYCLE_PRESETS,
  ];
  const current = pickValue(value);

  return (
    <MenuRadioGroup
      value={current}
      onValueChange={(next) => {
        if (typeof next !== "string" || next === current) return;
        if (next === INHERIT_VALUE) {
          onPick("inherit");
          return;
        }
        if (next === OFF_VALUE) {
          onPick("off");
          return;
        }
        const picked = cycleRows.find(
          (candidate) => cycleValue(candidate) === next,
        );
        if (picked) onPick(picked);
      }}
    >
      {/* A cycle is a choice, not a toggle: picking one closes the menu the
          way a select would. */}
      {inheritLabel ? (
        <MenuRadioItem value={INHERIT_VALUE} meta={inheritDetail} closeOnClick>
          {inheritLabel}
        </MenuRadioItem>
      ) : null}
      {cycleRows.map((cycle) => (
        <MenuRadioItem key={cycleValue(cycle)} value={cycleValue(cycle)} closeOnClick>
          {formatUpdateCycle(cycle)}
        </MenuRadioItem>
      ))}
      <MenuSeparator />
      <MenuRadioItem
        value={OFF_VALUE}
        disabled={offLocked}
        disabledReason={FOLLOW_UPS_OFF_LOCKED_REASON}
        closeOnClick
      >
        Don&rsquo;t require follow ups
      </MenuRadioItem>
    </MenuRadioGroup>
  );
}
